import React, { useState } from 'react';
import { ChevronLeft, Phone, CheckCircle } from 'lucide-react';

// Si formatPrice n'est pas passé en props, on le définit ici
const formatPrice = (xpf) => {
  if (typeof xpf !== 'number') return xpf;
  return xpf.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' XPF';
};

const FinancingPage = ({ navigate, setShowContactModal }) => {
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [duration, setDuration] = useState(6);
  const interestRate = 0.03;

  const handleInputChange = (e) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const val = Number(rawValue);
    if (val <= 100000000) setLoanAmount(val);
  };

  const calculateMonthlyPayment = (amount, months, rate) => {
    const monthlyRate = rate / 12;
    return (
      (amount * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1)
    );
  };

  const monthlyPayment = calculateMonthlyPayment(loanAmount, duration, interestRate);
  const totalAmount = monthlyPayment * duration;
  const totalInterest = totalAmount - loanAmount;

  const min = 300000;
  const max = 100000000;
  const percentage = ((loanAmount - min) / (max - min)) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-8 sm:py-12 md:py-16 mb-8 sm:mb-12">
  <div className="max-w-7xl mx-auto px-4">
    <button
      onClick={() => navigate("/")}
      className="flex items-center gap-2 mb-6 sm:mb-8 text-gray-400 hover:text-red-500 transition-colors uppercase text-xs font-black tracking-widest group"
    >
      <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
      Retour à l'accueil
    </button>

    <div className="flex items-center gap-4 sm:gap-6 mb-4 sm:mb-6">
      <div className="relative">
        <div className="text-4xl sm:text-6xl md:text-7xl">💰</div>
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-red-600 to-transparent rounded-full blur-3xl opacity-40" />
      </div>
      <div>
        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black italic uppercase tracking-tighter mb-2 sm:mb-3">
          Solutions de <span className="text-red-600">Financement</span>
        </h2>
        <p className="text-gray-400 font-medium text-sm sm:text-base md:text-lg">Calculez vos mensualités en quelques clics</p>
      </div>
    </div>

    <div className="flex flex-wrap gap-3 sm:gap-6 mt-6 sm:mt-8">
      <div className="flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl">
        <div className="text-lg sm:text-2xl font-black text-red-500">3%</div>
        <div className="text-xs sm:text-sm text-gray-300">Taux annuel</div>
      </div>
      <div className="flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl">
        <div className="text-lg sm:text-2xl font-black text-red-500">24h</div>
        <div className="text-xs sm:text-sm text-gray-300">Réponse rapide</div>
      </div>
    </div>
  </div>
</div>


      <div className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Simulateur */}
          <div className="bg-white rounded-2xl sm:rounded-[2rem] shadow-xl border border-gray-100 overflow-hidden">
  <div className="bg-gradient-to-r from-red-600 to-red-700 p-4 sm:p-6 text-white">
    <h3 className="text-xl sm:text-2xl font-black italic uppercase tracking-tight">Simulateur</h3>
  </div>
  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <label className="text-xs sm:text-sm font-black text-gray-700 uppercase tracking-widest">Montant du prêt</label>
        <div className="flex items-center gap-2 bg-gray-50 px-3 sm:px-4 py-2 rounded-xl sm:rounded-2xl border-2 border-transparent focus-within:border-red-600 transition-all">
          <input
            type="text"
            value={loanAmount.toLocaleString('fr-FR')} 
            onChange={handleInputChange}
            className="text-xl sm:text-2xl font-black text-red-600 bg-transparent outline-none w-32 sm:w-40 text-right"
          />
          <span className="text-lg sm:text-xl font-black text-red-600">XPF</span>
        </div>
                </div>
                <div className="relative flex items-center">
                  <input
                    type="range" min={min} max={max} step="50000" value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer slider-red"
                    style={{ background: `linear-gradient(to right, #dc2626 0%, #dc2626 ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)` }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-3 font-black uppercase tracking-tighter">
                  <span>Min: 300 000</span>
                  <span className="text-gray-900 bg-gray-100 px-2 py-1 rounded">Max: 100M</span>
                </div>
              </div>

              <div>
  <label className="block text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 sm:mb-4">Durée du prêt</label>
  <div className="grid grid-cols-3 gap-2 sm:gap-3">
    {[3, 6, 12].map((months) => (
      <button
        key={months} onClick={() => setDuration(months)}
        className={`relative p-3 sm:p-4 rounded-lg sm:rounded-xl font-bold text-sm transition-all duration-300 touch-manipulation ${duration === months ? "bg-red-600 text-white shadow-lg shadow-red-600/30 scale-105" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
      >
        {duration === months && <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center"><CheckCircle size={12} className="sm:w-4 sm:h-4 text-white" /></div>}
        <div className="text-xl sm:text-2xl mb-1">{months}</div>
        <div className="text-xs opacity-80">mois</div>
      </button>
    ))}
  </div>
</div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-red-100">
  <div className="text-center mb-4 sm:mb-6">
    <div className="text-xs sm:text-sm text-gray-500 font-bold uppercase tracking-wider mb-2">Mensualité estimée</div>
    <div className="text-3xl sm:text-4xl md:text-5xl font-black text-red-600 mb-1">{formatPrice(Math.round(monthlyPayment))}</div>
    <div className="text-xs sm:text-sm text-gray-500 font-medium">/mois</div>
  </div>
  <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6">
    <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
      <div className="text-[9px] sm:text-[10px] text-gray-500 font-bold uppercase mb-1">Total à rembourser</div>
      <div className="text-sm sm:text-base md:text-lg font-black text-gray-900">{formatPrice(Math.round(totalAmount))}</div>
    </div>
    <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
      <div className="text-[9px] sm:text-[10px] text-gray-500 font-bold uppercase mb-1">Coût du crédit</div>
      <div className="text-sm sm:text-base md:text-lg font-black text-gray-900">{formatPrice(Math.round(totalInterest))}</div>
    </div>
  </div>
</div>
            </div>
          </div>

          {/* Avantages & CTA */}
          <div className="space-y-6">
            <div className="bg-white rounded-[2rem] shadow-xl border border-gray-100 p-5 sm:p-8">
              <h3 className="text-2xl font-black italic uppercase text-gray-900 mb-6 tracking-tight">Nos <span className="text-red-600">Avantages</span></h3>
              <div className="space-y-4">
                {[
                  { icon: "💰", title: "Taux avantageux", desc: "Offres exclusives court terme.", color: "from-green-50 to-green-100" },
                  { icon: "⚡", title: "Réponse rapide", desc: "Accord de principe sous 24h.", color: "from-yellow-50 to-yellow-100" },
                  { icon: "📝", title: "Procédure simplifiée", desc: "Dossier clair et rapide.", color: "from-blue-50 to-blue-100" },
                ].map((item) => (
                  <div key={item.title} className={`flex gap-4 p-4 bg-gradient-to-br ${item.color} rounded-2xl border border-gray-100 hover:shadow-lg transition-all`}>
                    <div className="text-3xl flex-shrink-0">{item.icon}</div>
                    <div>
                      <h4 className="font-black text-gray-900 mb-1 uppercase text-sm tracking-tight">{item.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-[2rem] p-5 sm:p-8 text-white shadow-2xl relative overflow-hidden">
              <div className="relative z-10 text-center">
                <div className="text-5xl mb-4">🚀</div>
                <h3 className="text-2xl font-black italic uppercase mb-3">Prêt à démarrer ?</h3>
                <button 
                  onClick={() => setShowContactModal(true)}
                  className="w-full bg-white text-red-600 py-4 rounded-xl font-black uppercase text-sm tracking-widest hover:scale-105 transition-all flex items-center justify-center gap-3 shadow-lg"
                >
                  <Phone size={20} /> Contactez-nous
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancingPage;